import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, HelpCircle, ChevronDown, Truck, FileImage, Award, FileText, Clock, Package, AlertCircle } from 'lucide-react';

const FAQPage = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryId, itemId) => {
    const key = `${categoryId}-${itemId}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isOpen = (categoryId, itemId) => {
    return openItems[`${categoryId}-${itemId}`] || false;
  };

  const faqCategories = [
    {
      id: 'shipping',
      title: 'Shipping & Production Timelines',
      icon: Truck,
      description: 'Learn about our production times and shipping options',
      questions: [
        {
          id: 1,
          question: 'What are your shipping and production timelines?',
          answer: `Our production and shipping timelines vary depending on the product type and customization required:

**Standard Production Times:**
• Awards & Plaques: 3-5 business days
• Crystal Awards: 5-7 business days
• Name Badges & Plates: 2-3 business days
• Large Format Printing: 3-5 business days
• Promotional Items: 5-10 business days (varies by product)

**Rush Orders:**
Rush production is available for most products. Contact us for availability and pricing.

**Shipping Methods:**
• UPS Ground: 1-5 business days depending on location
• UPS 2-Day Air: 2 business days
• UPS Next Day Air: 1 business day
• Check cart for current shipping promotions

**Important Notes:**
• Production time begins after artwork approval
• Shipping times are estimates and may vary based on carrier conditions
• Orders placed after 2:00 PM PST may begin processing the next business day`
        },
        {
          id: 2,
          question: 'Do you offer expedited shipping?',
          answer: `Yes, we offer several expedited shipping options:

• **UPS Next Day Air:** Delivery by end of next business day
• **UPS 2-Day Air:** Delivery within 2 business days
• **UPS 3-Day Select:** Delivery within 3 business days

Expedited shipping costs are calculated at checkout based on package weight and destination. For urgent orders, please contact us directly to discuss rush production options.`
        },
        {
          id: 3,
          question: 'Do you ship internationally?',
          answer: `Currently, we primarily serve customers within the United States. For international shipping inquiries, please contact our sales team at sales@engraveca.com to discuss options and obtain a custom shipping quote.`
        },
        {
          id: 4,
          question: 'How can I track my order?',
          answer: `Once your order ships, you will receive a confirmation email with tracking information. You can track your package directly through the carrier's website (UPS, FedEx, or USPS). If you haven't received tracking information within the expected timeframe, please contact us.`
        }
      ]
    },
    {
      id: 'artwork',
      title: 'Artwork & File Formats',
      icon: FileImage,
      description: 'Accepted file formats and artwork requirements',
      questions: [
        {
          id: 1,
          question: 'What artwork or logo file formats do you accept?',
          answer: `We accept the following file formats for optimal results:

**Preferred Vector Formats (Best Quality):**
• Adobe Illustrator (.AI, .EPS)
• PDF (with fonts converted to outlines)
• CorelDRAW (.CDR)
• SVG

**Accepted Raster Formats:**
• TIFF (.TIF) - minimum 300 DPI
• PNG - minimum 300 DPI
• JPEG/JPG - minimum 300 DPI, high quality
• BMP - minimum 300 DPI

**Important Requirements:**
• All text should be converted to curves/outlines
• Minimum resolution of 300 DPI for raster images
• 1-bit black and white for laser engraving (no grayscale)
• CMYK color mode for full-color printing

**We Cannot Accept:**
• Low-resolution web images (72-96 DPI)
• Images embedded in Word documents, PowerPoint, or Excel
• GIF files (insufficient quality)
• Screenshots or photos of logos`
        },
        {
          id: 2,
          question: 'Why can\'t I use my logo from our website?',
          answer: `Website images are typically saved at 72-96 DPI (dots per inch) to optimize page loading speed. This resolution is too low for professional printing and engraving, which requires a minimum of 300 DPI.

Simply resaving a web image at a higher resolution does not improve the quality—it only makes the file larger while maintaining the same low-quality pixels.

**Solution:** Contact your company's marketing or graphics department to obtain the original high-resolution logo files. These are typically stored in AI, EPS, or high-resolution PDF format.`
        },
        {
          id: 3,
          question: 'What if I don\'t have a high-resolution logo?',
          answer: `If you don't have access to high-resolution artwork, we offer several solutions:

**Logo Recreation Service:**
Our graphic design team can recreate your logo from a low-resolution source. This service is billed at $55/hour in 15-minute increments.

**Stock Logo Database:**
We maintain a database of over 80,000 company logos. Your organization's logo may already be on file in an engravable format. Contact us to check availability.

**Important Note:**
If you proceed with a low-resolution image, please be aware that the final engraving or print quality may appear pixelated or unprofessional.`
        },
        {
          id: 4,
          question: 'Do different products have different artwork requirements?',
          answer: `Yes, artwork requirements can vary by product type:

**Laser Engraving (Awards, Plaques, Crystal):**
• 1-bit black and white artwork (no grayscale)
• Vector format preferred
• Clean, high-contrast designs work best

**Full-Color Printing (Banners, Signs, Promotional Items):**
• CMYK color mode
• 300 DPI minimum resolution
• Include bleed area if specified

**Sublimation (Drinkware, Apparel):**
• RGB color mode
• High-resolution PNG or TIFF
• Transparent backgrounds preferred

We will contact you if additional artwork modifications are needed for your specific product.`
        }
      ]
    },
    {
      id: 'engraving',
      title: 'Engraving Text Ideas',
      icon: Award,
      description: 'Inspiration for award inscriptions and recognition text',
      questions: [
        {
          id: 1,
          question: 'What should I engrave on an award?',
          answer: `A well-crafted award inscription typically includes:

**Standard Format:**
• Title/Award Name
• "Presented to" followed by recipient's name
• Reason for recognition
• Organization/Company name
• Date or Year

**Example:**
*Outstanding Achievement Award*
*Presented to*
*John Smith*
*In Recognition of Exceptional Leadership*
*and Dedication to Excellence*
*ABC Corporation*
*2026*`
        },
        {
          id: 2,
          question: 'Do you have sample text for achievement awards?',
          answer: `Here are some achievement award inscription ideas:

**General Achievement:**
"In Recognition of Outstanding Accomplishments and Dedication to Excellence"

**Sales Achievement:**
"Top Sales Achievement Award - For Exceptional Performance and Results"

**Team Excellence:**
"Team Excellence Award - In Recognition of Outstanding Collaboration and Achievement"

**Quality Award:**
"For Your Total Commitment to Quality and Continuous Improvement"

**Innovation Award:**
"In Recognition of Creative Vision and Innovative Solutions"`
        },
        {
          id: 3,
          question: 'What text works well for service awards?',
          answer: `Service award inscriptions should recognize tenure and contribution:

**Years of Service:**
"In Grateful Appreciation for [X] Years of Dedicated Service and Commitment"

**Retirement:**
"With Deep Appreciation for [X] Years of Outstanding Service - Enjoy Your Well-Deserved Retirement"

**Community Service:**
"In Recognition of Your Selfless Dedication to Our Community"

**Volunteer Recognition:**
"Thank You for Your Generous Contribution of Time and Talent"`
        },
        {
          id: 4,
          question: 'Do you have text ideas for leadership and coaching awards?',
          answer: `**Leadership Awards:**
"For Outstanding Leadership and Superior Performance"
"In Recognition of Visionary Guidance and Exceptional Leadership"
"For Your Ability to Inspire and Guide Others to Success"

**Coaching Awards:**
"Thank You for Spending Your Time Teaching, Inspiring, and Challenging Us"
"For Your Dedication and Patience in Helping Us Grow as a Team"
"A Hundred Years from Now, It Will Not Matter the House You Lived In... But the World Might Be a Better Place Because You Were Important in the Life of a Child"

**Sports Recognition:**
"Thank You Coach for Guiding Us Down the Field and Allowing Us to Grow as a Team"`
        },
        {
          id: 5,
          question: 'Can you help me write custom inscription text?',
          answer: `Absolutely! Our team is happy to help you craft the perfect inscription for your awards. When contacting us, please provide:

• The occasion or reason for the award
• Recipient's name and title (if applicable)
• Your organization's name
• Any specific achievements or qualities to highlight
• Preferred tone (formal, heartfelt, inspirational)
• Character/space limitations of your chosen award

Contact us at sales@engraveca.com or call +1 800 521 0527 for personalized assistance.`
        }
      ]
    },
    {
      id: 'policies',
      title: 'Store Policies',
      icon: FileText,
      description: 'Returns, cancellations, and order policies',
      questions: [
        {
          id: 1,
          question: 'What is your return and refund policy?',
          answer: `**Custom Product Policy:**
All orders are custom-made with personalized engraving, printing, or other customization. Due to the custom nature of our products, they cannot be returned or resold to other customers.

**Error Correction:**
If there is an error in personalization due to a mistake on our part, you may request a Return Merchandise Authorization (RMA) within 15 days of shipment. We will correct the error at our expense with a similar item.

**Important Notes:**
• We do not issue refunds for engraving errors—we will provide a corrected replacement
• Spelling errors submitted by the customer are the customer's responsibility
• A 25% restocking fee applies to any authorized returns
• Shipping charges are non-refundable

**RMA Process:**
1. Contact us to request an RMA number
2. RMA number must appear on the outside of the return package
3. Packages without visible RMA numbers will be refused`
        },
        {
          id: 2,
          question: 'How does the artwork approval process work?',
          answer: `**Proof Approval Process:**
Before any engraving or printing begins, you will receive a digital proof via email (PDF or image format) showing exactly how your product will look.

**Your Responsibilities:**
• Review the proof carefully for spelling, grammar, and layout
• Verify all names, dates, and details are correct
• Respond promptly to avoid production delays

**Important:**
• We cannot begin production until you approve the proof
• Once approved, you assume responsibility for any errors not caught during review
• Quick proof approval helps ensure on-time delivery`
        },
        {
          id: 3,
          question: 'What payment methods do you accept?',
          answer: `**Accepted Payment Methods:**
• Visa
• MasterCard
• American Express
• Discover
• Company checks (order processing begins upon receipt)

**Payment Terms:**
• All orders must be paid in full before production begins
• Check payments require clearance before order processing
• An order acknowledgment email confirms acceptance

**Returned Checks:**
A $35.00 service fee applies to returned checks. Replacement payment must be made via cashier's check.`
        },
        {
          id: 4,
          question: 'Can I cancel or modify my order?',
          answer: `**Before Production:**
If your order has not entered production, modifications may be possible. Contact us immediately at +1 800 521 0527.

**After Production Begins:**
Once engraving or printing has started, orders cannot be cancelled or modified due to the custom nature of the work.

**Proof Stage:**
Changes can be made during the proof approval stage before production begins. This is the best time to make corrections.

**Contact Us Promptly:**
The sooner you contact us about changes, the more likely we can accommodate your request.`
        },
        {
          id: 5,
          question: 'Do you guarantee your products?',
          answer: `**Quality Guarantee:**
We stand behind the quality of our workmanship. If you receive a product with:
• Manufacturing defects
• Incorrect engraving (our error)
• Damaged during production

We will replace the item at no cost to you.

**What's Not Covered:**
• Customer-provided incorrect information
• Approved proofs with errors
• Damage during shipping (file a claim with the carrier)
• Normal wear and tear

**Satisfaction Commitment:**
Your satisfaction is our priority. If you have any concerns about your order, please contact us within 15 days of receipt.`
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-ivory-50" data-testid="faq-page">
      {/* Header */}
      <div className="relative gradient-hero py-16 overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gold-500/5 rounded-full blur-[100px]" />
        <div className="container mx-auto px-4 relative z-10">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-ivory-500 hover:text-gold-600 mb-6 transition-colors font-body"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-gold-500/10 flex items-center justify-center">
              <HelpCircle className="w-7 h-7 text-gold-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-semibold text-ivory-800">Frequently Asked Questions</h1>
          </div>
          <p className="text-ivory-500 font-body max-w-2xl">
            Find answers to common questions about shipping, artwork requirements, engraving ideas, and our store policies.
          </p>
        </div>
      </div>

      {/* Quick Links */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {faqCategories.map((category) => (
              <a
                key={category.id}
                href={`#${category.id}`}
                className="p-4 rounded-xl bg-ivory-100 border border-ivory-300/50 hover:border-gold-500/40 transition-all group text-center shadow-luxury hover:shadow-luxury-lg"
              >
                <category.icon className="w-8 h-8 text-gold-600 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <span className="font-body font-medium text-ivory-700 group-hover:text-gold-700 transition-colors text-sm">
                  {category.title}
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="container mx-auto px-4 pb-16">
        <div className="max-w-4xl mx-auto space-y-12">
          {faqCategories.map((category) => (
            <section key={category.id} id={category.id} className="scroll-mt-24">
              {/* Category Header */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gold-500/10 flex items-center justify-center">
                  <category.icon className="w-6 h-6 text-gold-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-heading font-semibold text-ivory-800">{category.title}</h2>
                  <p className="text-ivory-500 text-sm font-body">{category.description}</p>
                </div>
              </div>

              {/* Questions */}
              <div className="space-y-3">
                {category.questions.map((item) => (
                  <div
                    key={item.id}
                    className="rounded-xl bg-ivory-100 border border-ivory-300/50 overflow-hidden shadow-luxury"
                    data-testid={`faq-item-${category.id}-${item.id}`}
                  >
                    <button
                      onClick={() => toggleItem(category.id, item.id)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-ivory-200/50 transition-colors"
                      aria-expanded={isOpen(category.id, item.id)}
                    >
                      <span className="font-body font-medium text-ivory-800 pr-4">{item.question}</span>
                      <ChevronDown 
                        className={`w-5 h-5 text-gold-600 flex-shrink-0 transition-transform duration-300 ${
                          isOpen(category.id, item.id) ? 'rotate-180' : ''
                        }`} 
                      />
                    </button>
                    
                    {isOpen(category.id, item.id) && (
                      <div className="px-6 pb-6 border-t border-ivory-200">
                        <div className="pt-4 text-ivory-600 font-body whitespace-pre-line leading-relaxed">
                          {item.answer}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>

      {/* Contact CTA */}
      <div className="container mx-auto px-4 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="p-8 rounded-xl gradient-champagne border border-gold-500/20 text-center">
            <AlertCircle className="w-10 h-10 text-gold-600 mx-auto mb-4" />
            <h3 className="text-xl font-heading font-semibold text-ivory-800 mb-2">
              Still Have Questions?
            </h3>
            <p className="text-ivory-600 font-body mb-6">
              Our customer service team is here to help. Contact us for personalized assistance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:support@engraveca.com" 
                className="inline-flex items-center justify-center gap-2 px-6 py-3 btn-luxury-primary rounded-lg font-body"
              >
                Email Support
              </a>
              <a 
                href="tel:+18005210527" 
                className="inline-flex items-center justify-center gap-2 px-6 py-3 btn-luxury-outline rounded-lg font-body"
              >
                Call +1 800 521 0527
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
